/**
 * Builds a perfect hedge-maze layout: an iterative recursive backtracker over a grid
 * of cells, carving passages until every cell has been visited exactly once.
 *
 * "Perfect" means exactly one path connects any two cells — solvability from entry to
 * exit is therefore structural, not something checked after the fact, and dead ends
 * are a natural by-product rather than something authored in. The backtracker is
 * iterative (an explicit stack, not function recursion) so a large grid — the 40×40
 * upper bound this module is tested against — cannot blow the call stack.
 *
 * Architectural rule 4: randomness is seeded. The only randomness source here is
 * `src/utils/seededRandom`, drawn once per maze from `mulberry32(seedFromString('maze:'
 * + seed))` — the same namespacing idiom `buildExterior.ts` uses for its own draws, so
 * a maze seed and (say) a wall-color seed can never collide even if a profile reuses
 * the same string for both. The same seed therefore always yields the same maze, on
 * every device and every launch, and a different seed always yields a different one.
 */

import { mulberry32, seedFromString } from '../../utils/seededRandom';

export type Maze = {
  readonly columns: number;
  readonly rows: number;
  /**
   * rows × (columns+1): verticalHedges[z][x] is the hedge on the west edge of cell
   * (x, z); x === columns is the east outer edge.
   */
  readonly verticalHedges: readonly (readonly boolean[])[];
  /**
   * (rows+1) × columns: horizontalHedges[z][x] is the hedge on the north edge of cell
   * (x, z); z === rows is the south outer edge.
   */
  readonly horizontalHedges: readonly (readonly boolean[])[];
  /** Gap in the south outer edge: horizontalHedges[rows][entryColumn] === false. */
  readonly entryColumn: number;
  /** Gap in the north outer edge: horizontalHedges[0][exitColumn] === false. */
  readonly exitColumn: number;
};

type Direction = 'north' | 'south' | 'east' | 'west';

const OFFSETS: Record<Direction, [dx: number, dz: number]> = {
  north: [0, -1],
  south: [0, 1],
  east: [1, 0],
  west: [-1, 0],
};

/** Knocks down the hedge shared between cell (x, z) and its neighbour in `direction`. */
function carve(
  verticalHedges: boolean[][],
  horizontalHedges: boolean[][],
  x: number,
  z: number,
  direction: Direction,
): void {
  switch (direction) {
    case 'north':
      horizontalHedges[z]![x] = false;
      break;
    case 'south':
      horizontalHedges[z + 1]![x] = false;
      break;
    case 'west':
      verticalHedges[z]![x] = false;
      break;
    case 'east':
      verticalHedges[z]![x + 1] = false;
      break;
  }
}

export function generateMaze(seed: string, columns: number, rows: number): Maze {
  const random = mulberry32(seedFromString('maze:' + seed));

  // Every hedge stands until carving opens a passage through it.
  const verticalHedges: boolean[][] = Array.from({ length: rows }, () =>
    new Array<boolean>(columns + 1).fill(true),
  );
  const horizontalHedges: boolean[][] = Array.from({ length: rows + 1 }, () =>
    new Array<boolean>(columns).fill(true),
  );

  const visited: boolean[][] = Array.from({ length: rows }, () => new Array<boolean>(columns).fill(false));

  // Explicit stack, not function recursion: a 40×40 grid must not overflow the call stack.
  const stack: [number, number][] = [[0, 0]];
  visited[0]![0] = true;

  while (stack.length > 0) {
    const [x, z] = stack[stack.length - 1]!;

    const unvisitedNeighbors: { direction: Direction; nx: number; nz: number }[] = [];
    for (const direction of Object.keys(OFFSETS) as Direction[]) {
      const [dx, dz] = OFFSETS[direction];
      const nx = x + dx;
      const nz = z + dz;
      if (nx >= 0 && nx < columns && nz >= 0 && nz < rows && !visited[nz]![nx]) {
        unvisitedNeighbors.push({ direction, nx, nz });
      }
    }

    if (unvisitedNeighbors.length === 0) {
      stack.pop();
      continue;
    }

    const choice = unvisitedNeighbors[Math.floor(random() * unvisitedNeighbors.length)]!;
    carve(verticalHedges, horizontalHedges, x, z, choice.direction);
    visited[choice.nz]![choice.nx] = true;
    stack.push([choice.nx, choice.nz]);
  }

  // Entry on the south outer edge, exit on the north outer edge, both drawn from the
  // same RNG stream so they stay reproducible alongside the carving itself.
  const entryColumn = Math.floor(random() * columns);
  const exitColumn = Math.floor(random() * columns);
  horizontalHedges[rows]![entryColumn] = false;
  horizontalHedges[0]![exitColumn] = false;

  return { columns, rows, verticalHedges, horizontalHedges, entryColumn, exitColumn };
}
